import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddPatientForm } from '../../ComponnentsUI/features/AddPatientForm';
import type { Patient } from '../../interfaces/patient';
import { PatientFormPageShell } from './PatientFormPageShell';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import type { PatientFormLocationState } from './patientFormPage.utils';

export const AddPatientPage = () => {
  const { t } = useTranslation('PatientManagement');
  const { t: tActions } = useTranslation('Actions');
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? null) as PatientFormLocationState | null;

  const addPatientFormRef = useRef<HTMLFormElement>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const returnTo = locationState?.returnTo ?? '/patients';

  useDocumentTitle(
    `DEEPECHO - ${t('modals.createPatient.title', { defaultValue: 'Add New Patient' })}`
  );

  const handlePatientCreated = (newPatient: Patient) => {
    const destination =
      locationState?.returnTo === '/patients' && newPatient?.id
        ? `/patients/${newPatient.id}`
        : returnTo;
    navigate(destination, { replace: true });
  };

  return (
    <PatientFormPageShell
      title={t('modals.createPatient.title', {
        defaultValue: tActions('addNewPatient'),
      })}
      subtitle={tActions('addNewPatientSubtitle')}
      cancelLabel={t('buttons.cancel')}
      submitLabel={t('modals.createPatient.form.submitButton', {
        defaultValue: tActions('save'),
      })}
      savingLabel={`${t('buttons.save', { defaultValue: tActions('save') })}...`}
      returnTo={returnTo}
      isSubmitting={isFormSubmitting}
      onSubmit={() => addPatientFormRef.current?.requestSubmit()}
    >
      <AddPatientForm
        ref={addPatientFormRef}
        onSubmittingChange={setIsFormSubmitting}
        onPatientCreated={handlePatientCreated}
        initialDataFromDicom={locationState?.initialDataFromDicom}
        selectedPacsStudy={locationState?.selectedPacsStudy}
      />
    </PatientFormPageShell>
  );
};

export default AddPatientPage;
